import { useEffect } from "react";
import * as Tone from "tone";
import { SampleKit } from "./useSamples";
import { VINYL_URL } from "./samples/sampleConfig";

export function useDSP(samples: SampleKit | null): void {
  useEffect(() => {
    if (!samples) return;

    let disposed = false;
    let scheduledId: number | null = null;
    const nodes: Tone.ToneAudioNode[] = [];

    const dest = Tone.getDestination();

    // Piano effects chain — Reverb is async (IR generation), set up after ready
    const filter = new Tone.Filter(4500, "lowpass", -12);
    const vibrato = new Tone.Vibrato({ frequency: 0.7, depth: 0.025 }); // tape wow & flutter
    const bitCrusher = new Tone.BitCrusher(12);
    const distortion = new Tone.Distortion(0.08); // light saturation
    const reverb = new Tone.Reverb({ decay: 2.5, preDelay: 0.01, wet: 0.35 });
    const sidechainGain = new Tone.Gain(1);
    nodes.push(filter, vibrato, bitCrusher, distortion, reverb, sidechainGain);

    reverb.ready.then(() => {
      if (disposed) return;

      samples.piano.disconnect();
      samples.piano.chain(filter, vibrato, bitCrusher, distortion, reverb, sidechainGain, dest);

      // Guitar — gentle lowpass + short reverb tail to blend with piano space
      const guitarFilter = new Tone.Filter(3500, "lowpass", -12);
      const guitarReverb = new Tone.Reverb({ decay: 1.2, preDelay: 0.01, wet: 0.25 });
      nodes.push(guitarFilter, guitarReverb);
      samples.guitar.volume.value = -6;
      samples.guitar.disconnect();
      guitarReverb.ready.then(() => {
        if (disposed) return;
        samples.guitar.chain(guitarFilter, guitarReverb, dest);
      });

      // Drums — lowpass to soften hi-hats/snare crack, short reverb tail
      const drumFilter = new Tone.Filter(6000, "lowpass", -12);
      const drumReverb = new Tone.Reverb({ decay: 0.6, wet: 0.2 });
      nodes.push(drumFilter, drumReverb);
      drumReverb.ready.then(() => {
        if (disposed) return;
        samples.drums.volume.value = -4;
        samples.drums.disconnect();
        samples.drums.chain(drumFilter, drumReverb, dest);
      });

      // Vinyl crackle loop
      if (VINYL_URL) {
        const vinylPlayer = new Tone.Player({ url: VINYL_URL, loop: true }).toDestination();
        vinylPlayer.volume.value = -28;
        nodes.push(vinylPlayer);
        Tone.loaded().then(() => { if (!disposed) vinylPlayer.start(); });
      }

      // Beat-synced sidechain: brief gain dip every half-note to simulate kick ducking the pads
      scheduledId = Tone.Transport.scheduleRepeat((time) => {
        sidechainGain.gain.setValueAtTime(0.55, time);
        sidechainGain.gain.exponentialRampToValueAtTime(1.0, time + 0.3);
      }, "2n");
    });

    return () => {
      disposed = true;
      if (scheduledId !== null) Tone.Transport.clear(scheduledId);
      nodes.forEach((n) => n.dispose());
    };
  }, [samples]);
}
